import React from "react";
import { Stack } from "@mui/material";
import { IContract } from "../../../../../../types/contract";
import {
  ContractExtractionResult,
  Paragraph,
} from "../../../../../../types/aiOutput";
import { DialogMode } from "../../../../../../types/dialogmode";
import { ISignee, ISignature } from "../../../../../../types/signature";
import { IDocument } from "../../../../../../types/document";
import { AIReadingStatus } from "../../../../../../types/contract";
import { IPrompt } from "../../../../../../types/prompt";
import { HumanDataVerification } from "../../../../../../types/humanDataVerification";
import { INSIGHTS_ANALYSIS_TAB_ITEMS } from "../types";

// Import all tab components
import BasicDetailsTab from "./BasicDetailsTab";
import TimelineTab from "./TimelineTab";
import VendorTab from "./VendorTab";
import TasksTab from "./TasksTab";
import DeviationsTab from "./DeviationsTab";
import FieldsFoundTab from "./FieldsFoundTab";
import FieldsNotFoundTab from "./FieldsNotFoundTab";
import FieldsExcludedTab from "./FieldsExcludedTab";
import FieldsSuggestedTab from "./FieldsSuggestedTab";
import ContractManagementTab from "./ContractManagementTab";
import AskMeAnythingTab from "./AskMeAnythingTab";
import FieldsNotAnalyzedTab from "./FieldsNotAnalyzedTab";
import RisksTab from "./RisksTab";

interface TabContentRendererProps {
  sideBarTab: INSIGHTS_ANALYSIS_TAB_ITEMS;
  contract: IContract;
  mode: DialogMode;
  signer?: ISignee;
  signees?: ISignee[];
  signatures?: ISignature[];
  document: IDocument;
  documents: IDocument[];
  aiOutputs: ContractExtractionResult[];
  analysisStatus?: AIReadingStatus;
  onAnalysis?: () => void;
  setSignees?: React.Dispatch<React.SetStateAction<Array<ISignee>>>;
  setSignatures?: React.Dispatch<React.SetStateAction<Array<ISignature>>>;
  resetSignatures?: React.Dispatch<React.SetStateAction<boolean>>;
  setBadge: (tabIndex: string, num: number) => void;
  badges: { [key: string]: number };
  isDownLg: boolean;
  prompts: IPrompt[];
  selectParagraph: (
    paragraph?: Paragraph,
    prompt?: IPrompt,
    aiOutput?: ContractExtractionResult
  ) => void;
  expanded: string | undefined;
  verifications?: HumanDataVerification[];
  suggestedPrompts: Partial<IPrompt & { relavantParagraphs: Paragraph[] }>[];
}

const TabContentRenderer: React.FC<TabContentRendererProps> = ({
  sideBarTab,
  contract,
  mode,
  signer,
  signees,
  signatures,
  document,
  documents,
  aiOutputs,
  analysisStatus,
  onAnalysis,
  setSignees,
  setSignatures,
  resetSignatures,
  setBadge,
  badges,
  isDownLg,
  prompts,
  selectParagraph,
  expanded,
  verifications,
  suggestedPrompts,
}) => {
  const renderTabContent = () => {
    switch (sideBarTab) {
      case INSIGHTS_ANALYSIS_TAB_ITEMS.CONTRACT_MANAGEMENT:
        return (
          <ContractManagementTab
            contract={contract}
            mode={mode}
            signer={signer}
            signees={signees}
            signatures={signatures}
            document={document}
            analysisStatus={analysisStatus}
            onAnalysis={onAnalysis}
            setSignees={setSignees}
            setSignatures={setSignatures}
            resetSignatures={resetSignatures}
            setBadge={setBadge}
            badges={badges}
            isDownLg={isDownLg}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.TIMELINE:
        return (
          <TimelineTab
            contract={contract}
            documents={documents}
            aiOutputs={aiOutputs}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.BASIC_DETAILS:
        return (
          <BasicDetailsTab
            contract={contract}
            mode={mode}
            aiOutputs={aiOutputs}
            documents={documents}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_ALL:
        return <RisksTab contract={contract} mode={mode} />;

      case INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_HIGH:
        return <RisksTab contract={contract} mode={mode} riskLevel="high" />;

      case INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_MEDIUM:
        return <RisksTab contract={contract} mode={mode} riskLevel="medium" />;

      case INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_LOW:
        return <RisksTab contract={contract} mode={mode} riskLevel="low" />;

      case INSIGHTS_ANALYSIS_TAB_ITEMS.VENDOR:
        return (
          <VendorTab
            contract={contract}
            aiOutputs={aiOutputs}
            documents={documents}
            mode={mode}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.TASKS:
        return <TasksTab contract={contract} />;

      case INSIGHTS_ANALYSIS_TAB_ITEMS.SCORES_V2:
        return <DeviationsTab contract={contract} aiOutputs={aiOutputs} />;

      case INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_FOUND:
        return (
          <FieldsFoundTab
            contract={contract}
            prompts={prompts}
            mode={mode}
            aiOutputs={aiOutputs}
            selectParagraph={selectParagraph}
            expanded={expanded}
            verifications={verifications}
            documents={documents}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_NOT_FOUND:
        return (
          <FieldsNotFoundTab
            contract={contract}
            prompts={prompts}
            mode={mode}
            aiOutputs={aiOutputs}
            selectParagraph={selectParagraph}
            expanded={expanded}
            verifications={verifications}
            documents={documents}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_NOT_ANALYZED:
        return (
          <FieldsNotAnalyzedTab
            contract={contract}
            prompts={prompts}
            mode={mode}
            aiOutputs={aiOutputs}
            selectParagraph={selectParagraph}
            expanded={expanded}
            verifications={verifications}
            documents={documents}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_EXCLUDED:
        return (
          <FieldsExcludedTab
            contract={contract}
            prompts={prompts}
            mode={mode}
            aiOutputs={aiOutputs}
            selectParagraph={selectParagraph}
            expanded={expanded}
            verifications={verifications}
            documents={documents}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_SUGGESTED:
        return (
          <FieldsSuggestedTab
            contract={contract}
            suggestedPrompts={suggestedPrompts}
            mode={mode}
            aiOutputs={aiOutputs}
            selectParagraph={selectParagraph}
            expanded={expanded}
            documents={documents}
          />
        );

      case INSIGHTS_ANALYSIS_TAB_ITEMS.AI_ASSISTANT:
        return <AskMeAnythingTab />;

      default:
        return null;
    }
  };

  return (
    <Stack
      sx={{
        padding: "0 20px",
      }}
    >
      {renderTabContent()}
    </Stack>
  );
};

export default TabContentRenderer;
